import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  MessageCircle,
  Send,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { CONTACT } from "@/data/siteData";
import { trackEvent } from "@/lib/analytics";

const FORM_ENDPOINT = `https://formsubmit.co/ajax/${CONTACT.email}`;

const REASONS = [
  "Informazioni su una visita",
  "Richiesta di prenotazione",
  "Second opinion",
  "Visita a domicilio",
  "Consulenza nutrizionale",
  "Altro",
];

export default function Contact() {
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const wa = `https://wa.me/39${CONTACT.whatsapp}`;

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    const form = e.target;
    const data = new FormData(form);

    if (!data.get("privacy")) {
      setStatus("error");
      setErrorMsg("Devi accettare l'informativa sulla privacy per inviare la richiesta.");
      return;
    }

    // FormSubmit ajax endpoint expects JSON
    const payload = {
      nome: data.get("nome"),
      email: data.get("email"),
      telefono: data.get("telefono") || "—",
      motivo: data.get("motivo"),
      messaggio: data.get("messaggio"),
      _subject: `Nuovo contatto dal sito — ${data.get("motivo")}`,
      _template: "table",
      _captcha: "false",
    };

    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && (json.success === "true" || json.success === true)) {
        setStatus("success");
        trackEvent("contact_form_submit", {
          event_category: "conversion",
          event_label: payload.motivo,
        });
        form.reset();
      } else {
        setStatus("error");
        setErrorMsg(
          json.message ||
            "Impossibile inviare la richiesta. Riprova o contattaci via WhatsApp.",
        );
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        "Errore di connessione. Riprova o contattaci via WhatsApp o email.",
      );
    }
  };

  return (
    <section id="contatti" className="relative py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-12 md:gap-16 items-start">
          {/* Left column: info */}
          <div className="md:col-span-2">
            <p className="overline">Contatti</p>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 text-[#0B4A5A] tracking-tight leading-tight">
              Hai un dubbio? Scrivimi.
            </h2>
            <div className="gold-line mt-8 w-16" />
            <p className="mt-8 text-gray-700 leading-relaxed">
              Compila il modulo per una richiesta di informazioni o
              prenotazione. Ti ricontatterò via email o telefono nel più breve
              tempo possibile.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={`mailto:${CONTACT.email}`}
                className="flex items-center gap-4 group"
                data-testid="contact-info-email"
              >
                <div className="w-11 h-11 rounded-full bg-[#0B4A5A]/6 text-[#0B4A5A] flex items-center justify-center group-hover:bg-[#C6A86B]/15 transition-colors">
                  <Mail size={17} strokeWidth={1.6} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-gray-400">
                    Email
                  </p>
                  <p className="text-[15px] text-gray-800 group-hover:text-[#0B4A5A] transition-colors">
                    {CONTACT.email}
                  </p>
                </div>
              </a>

              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 group"
                data-testid="contact-info-whatsapp"
              >
                <div className="w-11 h-11 rounded-full bg-[#0B4A5A]/6 text-[#0B4A5A] flex items-center justify-center group-hover:bg-[#C6A86B]/15 transition-colors">
                  <MessageCircle size={17} strokeWidth={1.6} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-gray-400">
                    WhatsApp · Telefono
                  </p>
                  <p className="text-[15px] text-gray-800 group-hover:text-[#0B4A5A] transition-colors">
                    {CONTACT.whatsappDisplay}
                  </p>
                </div>
              </a>
            </div>
          </div>

          {/* Right column: form */}
          <div className="md:col-span-3">
            {status === "success" ? (
              <div
                data-testid="contact-form-success"
                className="rounded-2xl border border-[#C6A86B]/30 bg-[#F9FAFB] p-10 flex flex-col items-center text-center min-h-[400px] justify-center"
              >
                <div className="w-14 h-14 rounded-full bg-[#C6A86B]/15 text-[#C6A86B] flex items-center justify-center">
                  <CheckCircle2 size={26} strokeWidth={1.6} />
                </div>
                <h3 className="font-serif text-2xl md:text-3xl mt-6 text-[#0B4A5A]">
                  Richiesta inviata correttamente.
                </h3>
                <p className="mt-4 text-gray-600 max-w-md leading-relaxed">
                  Grazie per avermi scritto. Riceverò la tua richiesta e ti
                  ricontatterò nel più breve tempo possibile.
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  data-testid="contact-form-reset"
                  className="btn-secondary mt-8"
                >
                  Invia una nuova richiesta
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="bg-white rounded-2xl border border-gray-100 p-8 md:p-10 shadow-[0_10px_40px_rgba(11,74,90,0.04)]"
                noValidate
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label
                      htmlFor="nome"
                      className="text-xs uppercase tracking-[0.16em] text-gray-500 font-medium"
                    >
                      Nome e cognome *
                    </label>
                    <input
                      id="nome"
                      name="nome"
                      type="text"
                      required
                      data-testid="contact-input-nome"
                      className="mt-2 w-full bg-transparent border-b border-gray-200 py-3 text-[15px] text-gray-800 focus:border-[#0B4A5A] focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="text-xs uppercase tracking-[0.16em] text-gray-500 font-medium"
                    >
                      Email *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      data-testid="contact-input-email"
                      className="mt-2 w-full bg-transparent border-b border-gray-200 py-3 text-[15px] text-gray-800 focus:border-[#0B4A5A] focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="telefono"
                      className="text-xs uppercase tracking-[0.16em] text-gray-500 font-medium"
                    >
                      Telefono
                    </label>
                    <input
                      id="telefono"
                      name="telefono"
                      type="tel"
                      data-testid="contact-input-telefono"
                      className="mt-2 w-full bg-transparent border-b border-gray-200 py-3 text-[15px] text-gray-800 focus:border-[#0B4A5A] focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="motivo"
                      className="text-xs uppercase tracking-[0.16em] text-gray-500 font-medium"
                    >
                      Motivo *
                    </label>
                    <select
                      id="motivo"
                      name="motivo"
                      required
                      defaultValue=""
                      data-testid="contact-input-motivo"
                      className="mt-2 w-full bg-transparent border-b border-gray-200 py-3 text-[15px] text-gray-800 focus:border-[#0B4A5A] focus:outline-none transition-colors appearance-none cursor-pointer"
                    >
                      <option value="" disabled>
                        Seleziona…
                      </option>
                      {REASONS.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-6">
                  <label
                    htmlFor="messaggio"
                    className="text-xs uppercase tracking-[0.16em] text-gray-500 font-medium"
                  >
                    Il tuo messaggio *
                  </label>
                  <textarea
                    id="messaggio"
                    name="messaggio"
                    rows={5}
                    required
                    data-testid="contact-input-messaggio"
                    placeholder="Descrivi brevemente la tua richiesta o i sintomi che vorresti approfondire…"
                    className="mt-2 w-full bg-transparent border-b border-gray-200 py-3 text-[15px] text-gray-800 focus:border-[#0B4A5A] focus:outline-none transition-colors resize-none"
                  />
                </div>

                <label className="mt-6 flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="privacy"
                    required
                    data-testid="contact-input-privacy"
                    className="mt-1 w-4 h-4 accent-[#0B4A5A] cursor-pointer flex-shrink-0"
                  />
                  <span className="text-sm text-gray-600 leading-relaxed">
                    Ho letto e accetto l'
                    <Link
                      to="/privacy"
                      className="text-[#0B4A5A] font-medium link-underline"
                    >
                      informativa sulla privacy
                    </Link>{" "}
                    e autorizzo il trattamento dei miei dati personali per la
                    gestione di questa richiesta (art. 6 e 9 GDPR).
                  </span>
                </label>

                {status === "error" && (
                  <div
                    role="alert"
                    data-testid="contact-form-error"
                    className="mt-5 flex items-start gap-2 p-4 rounded-lg bg-red-50 border border-red-100 text-sm text-red-700"
                  >
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{errorMsg}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="contact-form-submit"
                  className="mt-8 btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Send size={16} strokeWidth={1.6} />
                  {status === "sending" ? "Invio in corso…" : "Invia richiesta"}
                </button>

                <p className="mt-4 text-xs text-gray-400 leading-relaxed">
                  * Campi obbligatori. Non condividiamo mai i tuoi dati con
                  terze parti a scopo commerciale.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
